/**
 * 机上調査の調査の切り替えと、追加の調査の管理 (docs/98 R-97)。
 *
 * - 切り替え: 表示・集計・解析に使う調査を選ぶ (URL の `?survey=`)。誰でも使える。
 * - 管理 (master のみ): 追加の調査 (表示名・解析設定・机上調査テンプレート) の追加・変更・削除。
 *   削除しても地点に保存した結果と解析の記録は消えない (同じ key で追加し直せば表示される)。
 */
import { Button, useToast } from '@citizen-walk/shared-ui';
import {
  isValidStreetSurveyKey,
  listAdditionalStreetSurveys,
  type AiAnalysisSettings,
  type District,
  type StreetSurveyDef,
} from '@citizen-walk/shared-types';
import { collection, doc, getDocs, serverTimestamp, setDoc } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { db } from '../../lib/firebase';
import type { StreetSurveySelection } from './streetSurveySurveys';
import { useStreetSurveyTemplateOptions } from './useStreetSurveyTemplateOptions';

export interface StreetSurveySurveysPanelProps {
  districtId: string;
  district: District | null;
  selection: StreetSurveySelection;
  canManage: boolean;
  uid: string | null;
}

interface SettingsOption {
  id: string;
  name: string;
}

function useSettingsOptions(enabled: boolean): SettingsOption[] {
  const [items, setItems] = useState<SettingsOption[]>([]);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    getDocs(collection(db, 'aiAnalysisSettings'))
      .then((snap) => {
        if (cancelled) return;
        setItems(
          snap.docs
            .map((d) => ({ ...(d.data() as AiAnalysisSettings), id: d.id }))
            .filter((s) => !s.archivedAt)
            .map((s) => ({ id: s.id, name: s.displayName || '名称なし' }))
            .sort((a, b) => a.name.localeCompare(b.name, 'ja')),
        );
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);
  return items;
}

interface Draft {
  /** 変更中の調査の key (新規は null)。 */
  editingKey: string | null;
  label: string;
  key: string;
  settingsId: string;
  templateId: string;
}

const EMPTY_DRAFT: Draft = { editingKey: null, label: '', key: '', settingsId: '', templateId: '' };
const inputClass = 'mt-0.5 block w-full rounded border border-slate-300 px-2 py-1 text-sm bg-white';

export function StreetSurveySurveysPanel({
  districtId,
  district,
  selection,
  canManage,
  uid,
}: StreetSurveySurveysPanelProps) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const settingsOptions = useSettingsOptions(canManage && open);
  const templates = useStreetSurveyTemplateOptions(canManage && open);
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const surveys = listAdditionalStreetSurveys(district?.aiAnalysis);

  if (!canManage && selection.options.length <= 1) return null;

  const keyError =
    draft.editingKey === null
      ? !draft.key
        ? 'key を入力してください。'
        : !isValidStreetSurveyKey(draft.key)
          ? 'key は半角英小文字・数字をハイフンで区切って入力してください（default は使えません）。'
          : surveys.some((s) => s.key === draft.key)
            ? '同じ key の調査があります。'
            : null
      : null;
  const errors = [
    ...(!draft.label.trim() ? ['表示名を入力してください。'] : []),
    ...(keyError ? [keyError] : []),
    ...(!draft.settingsId ? ['解析設定を選んでください。'] : []),
  ];

  async function writeSurveys(next: StreetSurveyDef[], message: string) {
    if (!uid) return;
    setSaving(true);
    try {
      await setDoc(
        doc(db, 'districts', districtId),
        { aiAnalysis: { streetSurveys: next }, updatedAt: serverTimestamp(), updatedBy: uid },
        { merge: true },
      );
      toast('success', message);
      setDraft(EMPTY_DRAFT);
    } catch (e) {
      toast('error', `保存に失敗しました: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  async function save() {
    if (errors.length > 0 || saving) return;
    const entry: Partial<StreetSurveyDef> = {
      label: draft.label.trim(),
      settingsId: draft.settingsId,
      streetSurveyTemplateId: draft.templateId || null,
    };
    if (draft.editingKey === null) {
      await writeSurveys(
        [...surveys, { key: draft.key, ...entry } as StreetSurveyDef],
        `調査「${entry.label}」を追加しました。`,
      );
    } else {
      await writeSurveys(
        surveys.map((s) => (s.key === draft.editingKey ? { ...s, ...entry } : s)),
        `調査「${entry.label}」を保存しました。`,
      );
    }
  }

  async function remove(survey: StreetSurveyDef) {
    if (
      !confirm(
        `調査「${survey.label}」を一覧から外しますか？地点に保存した結果と解析の記録は消えません（同じ key で追加し直すと表示されます）。`,
      )
    ) {
      return;
    }
    if (selection.surveyKey === survey.key) selection.setSurveyKey('default');
    await writeSurveys(
      surveys.filter((s) => s.key !== survey.key),
      `調査「${survey.label}」を外しました。`,
    );
  }

  return (
    <div className="mb-3 rounded border border-slate-200 bg-white px-3 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex flex-wrap items-center gap-2 text-sm text-slate-700 min-w-0">
          <span className="font-semibold">調査</span>
          <select
            className="rounded border border-slate-300 px-2 py-1 text-sm bg-white"
            style={{ maxWidth: '100%' }}
            value={selection.surveyKey}
            onChange={(e) => selection.setSurveyKey(e.target.value)}
          >
            {selection.options.map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
                {o.isDefault ? '（既定）' : ''}
              </option>
            ))}
          </select>
        </label>
        {!selection.isDefault && (
          <span className="text-xs text-slate-500">
            この調査の設定で解析し、結果を切り替えて表示します。新しい地点を作ると、既定の調査ではまだ解析していない地点として並びます。近く（10m
            以内）に既存の地点があるときは、その地点をこの調査で解析します。
          </span>
        )}
        {canManage && (
          <Button size="sm" variant="secondary" onClick={() => setOpen((v) => !v)}>
            {open ? '調査の管理を閉じる' : '調査の追加・変更'}
          </Button>
        )}
      </div>

      {canManage && open && (
        <div className="mt-3 grid grid-cols-1 gap-3 border-t border-slate-200 pt-3">
          <p className="text-xs text-slate-600">
            同じ地点を、ウォーカブルと防災のように別の解析設定・机上調査テンプレートで解析して切り替えます。既定の調査は地区の
            AI 解析設定です。追加の調査では、地区の AI
            解析設定の上書き（出力フォーマットの差し替えなど）は使いません。
          </p>
          {surveys.length > 0 ? (
            <ul className="grid grid-cols-1 gap-1 text-sm">
              {surveys.map((s) => (
                <li key={s.key} className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-slate-800">{s.label}</span>
                  <span className="text-xs text-slate-500">
                    解析設定:{' '}
                    {settingsOptions.find((o) => o.id === s.settingsId)?.name ?? '読み込み中'}
                    {s.streetSurveyTemplateId &&
                      `・テンプレート: ${templates.find((t) => t.id === s.streetSurveyTemplateId)?.displayName ?? '読み込み中'}`}
                  </span>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={saving}
                    onClick={() =>
                      setDraft({
                        editingKey: s.key,
                        key: s.key,
                        label: s.label,
                        settingsId: s.settingsId,
                        templateId: s.streetSurveyTemplateId ?? '',
                      })
                    }
                  >
                    変更
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    disabled={saving}
                    onClick={() => void remove(s)}
                  >
                    外す
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-slate-500">追加の調査はまだありません。</p>
          )}

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <label className="block text-xs text-slate-600 min-w-0">
              表示名
              <input
                className={inputClass}
                value={draft.label}
                placeholder="例: 防災"
                onChange={(e) => setDraft({ ...draft, label: e.target.value })}
              />
            </label>
            <label className="block text-xs text-slate-600 min-w-0">
              key（結果の保存に使う名前。追加後は変えられません）
              <input
                className={inputClass}
                value={draft.key}
                disabled={draft.editingKey !== null}
                placeholder="例: bousai"
                onChange={(e) => setDraft({ ...draft, key: e.target.value.trim() })}
              />
            </label>
            <label className="block text-xs text-slate-600 min-w-0">
              解析設定
              <select
                className={inputClass}
                style={{ maxWidth: '100%' }}
                value={draft.settingsId}
                onChange={(e) => setDraft({ ...draft, settingsId: e.target.value })}
              >
                <option value="">選んでください</option>
                {settingsOptions.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-xs text-slate-600 min-w-0">
              机上調査テンプレート
              <select
                className={inputClass}
                style={{ maxWidth: '100%' }}
                value={draft.templateId}
                onChange={(e) => setDraft({ ...draft, templateId: e.target.value })}
              >
                <option value="">テンプレートを使わない</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.displayName}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {errors.length > 0 && (draft.label || draft.key || draft.settingsId) && (
            <ul className="list-disc pl-5 text-xs text-danger-700">
              {errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          )}
          <div className="flex justify-end gap-2">
            {draft.editingKey !== null && (
              <Button size="sm" variant="secondary" onClick={() => setDraft(EMPTY_DRAFT)}>
                キャンセル
              </Button>
            )}
            <Button size="sm" onClick={() => void save()} disabled={saving || errors.length > 0}>
              {draft.editingKey === null ? '調査を追加' : '保存'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
